import { getApiUrl } from "@/utils/api";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = getApiUrl();

export default function AdminResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ new_password: "", confirm: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Reset link is missing — request a new one from the login page.");
      return;
    }
    if (form.new_password.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/auth/reset-password`, {
        token,
        new_password: form.new_password,
      });
      setDone(true);
      toast.success("Password reset — please sign in with your new password");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30" data-testid="admin-reset-password-page">
      <div className="w-full max-w-md p-8">
        <div className="glass-card p-10 space-y-6">
          {done ? (
            <div className="space-y-5 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
              <h1 className="font-serif text-2xl font-medium">Password reset</h1>
              <p className="text-sm text-muted-foreground">
                Your password has been updated. Please sign in to continue.
              </p>
              <Button
                onClick={() => navigate("/admin/login", { replace: true })}
                className="w-full h-12 rounded-none"
                data-testid="reset-go-to-login-btn"
              >
                Go to Sign In
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center space-y-2">
                <h1 className="font-serif text-2xl font-medium">Reset your password</h1>
                <p className="text-sm text-muted-foreground">
                  Enter a new password for your master admin account.
                </p>
              </div>

              {!token && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3">
                  This page is missing a reset token. Please request a new reset link from the login page.
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-new-password">New password</Label>
                  <div className="relative">
                    <Input
                      id="reset-new-password"
                      type={showPassword ? "text" : "password"}
                      value={form.new_password}
                      onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                      className="h-12 rounded-none pr-12"
                      autoComplete="new-password"
                      data-testid="reset-new-password-input"
                      disabled={submitting || !token}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-confirm-password">Confirm password</Label>
                  <Input
                    id="reset-confirm-password"
                    type={showPassword ? "text" : "password"}
                    value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    className="h-12 rounded-none"
                    autoComplete="new-password"
                    data-testid="reset-confirm-password-input"
                    disabled={submitting || !token}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting || !token || !form.new_password || !form.confirm}
                  className="w-full h-12 rounded-none"
                  data-testid="reset-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating…</>
                  ) : (
                    "Reset password"
                  )}
                </Button>
              </form>
            </>
          )}
        </div>

        <div className="mt-6 text-center">
          <a
            href="/admin/login"
            className="text-sm text-muted-foreground hover:text-foreground link-underline"
            data-testid="reset-back-to-login"
          >
            ← Back to sign in
          </a>
        </div>
      </div>
    </div>
  );
}
